import { Tooltip } from '../ui/Tooltip.js'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * When something happened, as a person would say it.
 *
 * **The exact time is always one hover away.** A desk reading "3 days ago" on a
 * refund is reading something it may have to put in an email, and a relative time
 * with no absolute behind it is a figure nobody can quote.
 *
 * `never` is a word, not a blank: *"First signed in — Never"* is a fact about a
 * professional who has not taken up their invitation, and an empty cell there
 * reads as a row that failed to load.
 */
export function RelativeTime({
  iso,
  never = 'Never',
  zone,
  className,
}: {
  iso: string | null | undefined
  never?: string
  /**
   * Whose clock the exact time is read on.
   *
   * *"3 days ago"* is the same sentence everywhere, so most of this needs no
   * zone. An **appointment** does: it belongs to the professional's clock,
   * and the desk was putting the reader's browser zone in the tooltip — so
   * a support agent in Toronto hovering a Vancouver appointment was shown,
   * and quoted, a time neither the client nor the professional had.
   *
   * Given, the tooltip reads in that zone and names it. Left out, it reads
   * in the reader's, which is right for a thing that *happened* — a refund,
   * a sign-in — because those are moments rather than appointments.
   */
  zone?: string
  className?: string
}) {
  if (!iso) return <span className={className}>{never}</span>

  const then = new Date(iso)
  const delta = Date.now() - then.getTime()
  const exact = then.toLocaleString('en-CA', {
    ...(zone ? { timeZone: zone } : {}),
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(zone ? { timeZoneName: 'short' as const } : {}),
  })

  return (
    <Tooltip label={exact}>
      <time dateTime={iso} className={className}>
        {say(delta, then)}
      </time>
    </Tooltip>
  )
}

function say(delta: number, then: Date): string {
  if (delta < 0) {
    // In the future — a scheduled class, a closure date somebody was emailed.
    const ahead = -delta
    if (ahead < HOUR) return ahead_(Math.max(1, Math.round(ahead / MINUTE)), 'minute')
    if (ahead < DAY) return ahead_(Math.round(ahead / HOUR), 'hour')
    if (ahead < 7 * DAY) return ahead_(Math.round(ahead / DAY), 'day')
    return then.toLocaleDateString('en-CA', { day: 'numeric', month: 'short', year: 'numeric' })
  }
  if (delta < MINUTE) return 'just now'
  if (delta < HOUR) return plural(Math.round(delta / MINUTE), 'minute')
  if (delta < DAY) return plural(Math.round(delta / HOUR), 'hour')
  if (delta < 7 * DAY) return plural(Math.round(delta / DAY), 'day')
  // Past a week, the date itself is more use than a count of days.
  return then.toLocaleDateString('en-CA', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * One day, not *"1 days"*.
 *
 * Every table on the desk has a date column and every one of them read
 * "1 days ago" for anything between eighteen and thirty-six hours old.
 */
const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`
const ahead_ = (n: number, unit: string) => `in ${n} ${unit}${n === 1 ? '' : 's'}`
